import { useCallback, useRef, useState } from 'react';
import { apiService } from '../../../services/api';

/**
 * Turn the author's question into a query, and hand the comparison its columns.
 *
 * **This is the seam the whole of PRD-1313 turns on.** Before it, an alert had
 * two definitions: a query the author previewed, and a comparison beside it
 * that named physical tables and columns and ignored the query entirely. The
 * two could disagree, and when they did the alert ran on the comparison and the
 * author reviewed the preview.
 *
 * Here there is one definition. The question produces the query, the query
 * produces the columns, and the comparison can only name columns that came back
 * — so there is nothing for the two halves to disagree about.
 *
 * **Explicitly invoked, not run on every keystroke.** Each call is a real query
 * against the structured-data service. It is also the step where the author's
 * sentence stops being editable prose and becomes the thing the alert runs, so
 * an explicit action is the honest shape: the pre-filled comparison below is a
 * suggestion they confirm or change, which is what the ticket asks for.
 */
export const PREVIEW_ROW_CAP = 20;

/**
 * The comparison this query suggests, as a starting point.
 *
 * **Suggestions, never decisions.** Every value here lands in a control the
 * author can change, and none of it is stored until they move on.
 *
 * **It reads the result now, because there is no longer a document to read
 * (PRD-1405).** Version 2 read the IR's `select`: an aggregate projection was a
 * number the query worked out, and `subject` / `period` were aliases the
 * vocabulary reserved. None of that survives storing SQL — the console does not
 * parse SQL, and a generator no longer constrained by a grammar is under no
 * obligation to alias anything in particular.
 *
 * So: the reserved names first, where the generator still uses them, then the
 * types in the rows it returned. A column whose sampled values are numbers is
 * the only kind a threshold means anything against; one whose values are not is
 * the only kind that identifies a subject.
 *
 * **Typed, not parsed.** A numeric *string* is left alone deliberately — an id
 * that happens to be digits is not a measure, and psycopg hands real numerics
 * back as numbers, so the distinction is the driver's rather than a guess of
 * ours.
 *
 * Silent when it cannot tell, which is the rule this function has always
 * followed and matters more now that it is guessing from less. A wrong pre-fill
 * in a control the author skims past is worse than an empty one they have to
 * fill: the empty one blocks the step and the wrong one does not.
 */
export const suggestComparison = (columns, rows = []) => {
  const names = (columns ?? [])
    .map((c) => (typeof c === 'string' ? c : c?.name))
    .filter(Boolean);
  const offered = (name) => (name && names.includes(name) ? name : '');

  const sampled = Array.isArray(rows) ? rows.slice(0, 20) : [];
  const values = (name) => sampled
    .map((r) => r?.[name])
    .filter((v) => v !== null && v !== undefined);
  // Unsampled columns answer neither way, so an empty result suggests nothing
  // rather than suggesting the first column of each kind.
  const isNumeric = (name) => {
    const seen = values(name);
    return seen.length > 0 && seen.every((v) => typeof v === 'number' && Number.isFinite(v));
  };
  const isNotNumeric = (name) => {
    const seen = values(name);
    return seen.length > 0 && seen.every((v) => typeof v !== 'number');
  };

  const subject = names.find((n) => n === 'subject') ?? names.find(isNotNumeric);
  // **Excluding the subject, which is not a fussy detail.** `SELECT member_id AS
  // subject, COUNT(*) AS total` is the ordinary shape, and psycopg hands a
  // numeric member id back as a number — so the reserved `subject` alias is also
  // the first numeric column, and without this the same column was pre-filled as
  // both. The threshold would then be compared against member ids, and nothing
  // downstream objects because both names are in the result. Version 2 could not
  // reach this: the measure had to be an aggregate projection. Raised in review
  // of this branch.
  const measure = names.filter((n) => n !== subject).find(isNumeric);
  // `PERIOD_ALIAS` — the alias DATE_TRUNC buckets under. Matched by name only:
  // a date column is not necessarily the one a run counts over, and offering the
  // wrong one silently changes what "consecutive" means.
  const period = names.find((n) => n === 'period');

  return {
    subjectColumn: offered(subject),
    measureColumn: offered(measure),
    periodColumn: offered(period),
  };
};

/**
 * How long a build may take before the wait is named.
 *
 * The generation loop was measured at 69-92 seconds per sentence on a real
 * dataset, so a wait past this point is normal and a wait the author is not
 * told about reads as a hang. Short enough to land well before the fast end of
 * that range. Raised in review of #177.
 */
export const SLOW_BUILD_MS = 8000;

/**
 * The most values the endpoint takes in each of `confirmedValues` and `acceptedValues`. A
 * longer list is refused as a plain 422 with no `reason`, so the author would see the generic
 * build error instead of the refusal they were answering. The newest are kept: they answer
 * the refusal on screen.
 */
export const MAX_CHOSEN_VALUES = 8;

/** `earlier` and `values` together, each `{column, asked, used}` once (case aside), in order. */
export const mergeAccepted = (earlier = [], values = []) => {
  const seen = new Set();
  return [...earlier, ...values].filter((p) => {
    if (!p || !['column', 'asked', 'used'].every((k) => typeof p[k] === 'string')) return false;
    const key = [p.column, p.asked, p.used].map((v) => v.toLowerCase()).join('\u0000');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(-MAX_CHOSEN_VALUES);
};

/**
 * `earlier` and `values` together, each `{column, value}` pair once (case aside), in the
 * order they were confirmed.
 */
export const mergeConfirmed = (earlier = [], values = []) => {
  const seen = new Set();
  return [...earlier, ...values].filter((p) => {
    if (!p || typeof p.column !== 'string' || typeof p.value !== 'string') return false;
    const key = `${p.column.toLowerCase()}\u0000${p.value.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(-MAX_CHOSEN_VALUES);
};

/** The values chosen so far for `question`, or none when they were chosen for another. */
const choicesFor = (choices, question) => (choices?.question === question
  ? { values: choices.values ?? [], accepted: choices.accepted ?? [] }
  : { values: [], accepted: [] });

/**
 * @param {Function} onSetFormData the wizard's setter
 * @param {{question: string, values: Array, accepted: Array}|null} choices the values already
 *   kept or chosen, as `formData.alert.authorChoices` holds them
 * @returns {{ run: Function, build: Function, confirm: Function, accept: Function,
 *   loading: boolean, slow: boolean, error: string|null }}
 */
export const useAlertIrAuthor = (onSetFormData, choices = null) => {
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState(null);
  // Only the newest call may write. Without this a slow first request could land
  // after a fast second one and overwrite the newer query with the older one —
  // and the columns underneath the comparison would then belong to a question
  // the author had already replaced.
  const runId = useRef(0);

  // **No timezone.** The endpoint takes an `anchorDate` and not a zone, so a
  // timezone accepted here would be one the caller believed was in force and
  // that reached nothing — the "looks configured and silently is not" failure
  // this whole epic exists to remove. The anchor is left unset deliberately:
  // authoring previews against today, and the alert's own zone applies when it
  // runs, which is where `Scheduler.timezone` already lives.
  const run = useCallback(async ({ question, datasetIds, confirmedValues = [], acceptedValues = [] }) => {
    const id = runId.current + 1;
    runId.current = id;
    setLoading(true);
    setSlow(false);
    setError(null);
    // Cleared in `finally` alongside `loading`, and guarded by the same run-id
    // check, so a superseded call cannot announce a slow build over a newer one.
    const slowTimer = setTimeout(() => {
      if (runId.current === id) setSlow(true);
    }, SLOW_BUILD_MS);

    try {
      const data = await apiService.authorAlertIR({
        question,
        datasetIds,
        previewRows: PREVIEW_ROW_CAP,
        confirmedValues,
        acceptedValues,
      });
      if (runId.current !== id) return;

      const columns = data?.columns ?? [];
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const suggestion = suggestComparison(columns, rows);
      onSetFormData((prev) => {
        const alert = prev.alert ?? {};
        return {
          ...prev,
          alert: {
            // **The query, and what binds its window — not the IR (PRD-1405).**
            //
            // These are the two halves of one fact and they are stored together
            // for that reason: `sql` holds its window as `%(window_start)s` /
            // `%(window_end)s`, and `window` is what those bind to. Carrying one
            // without the other produces a document the API refuses, in either
            // direction, and it refuses rather than storing it precisely because
            // the failure would otherwise land on every scheduled run.
            //
            // `window` is null for a query with no date filter at all, which is
            // the common shape rather than the exception.
            ...alert,
            sql: data?.sql ?? null,
            // **The reference sent back in place of the query** (PRD-1438).
            // `sql` is still held because the step still validates against it;
            // what changes is that the payloads quote this instead.
            queryDraftId: data?.draftId ?? null,
            window: data?.window ?? null,
            resultColumns: columns,
            authorRefusal: null,
            // **Only where the author has not already chosen.** Re-running the
            // question after picking a measure must not silently move the
            // measure back to the suggestion — that is the alert changing
            // underneath someone who did not ask for it.
            subjectColumn: alert.subjectColumn || suggestion.subjectColumn,
            measureColumn: alert.measureColumn || suggestion.measureColumn,
            periodColumn: alert.periodColumn || suggestion.periodColumn,
            // **The rows the comparison's columns come from.**
            //
            // Dropped in self-review on the grounds that nothing read them and
            // that two counts on one screen invite calibrating a threshold
            // against the wrong one. The first half was true and is now the
            // problem: nothing read them, so after Build the preview panel still
            // read "Run or select a query to see preview" and the author chose
            // columns out of a result they had never seen. The only thing that
            // *would* show them was REGENERATE, which runs the chat query — the
            // second definition of the alert, back again one layer up.
            //
            // The second half was a real risk and is answered by where these are
            // rendered rather than by discarding them: `AlertQueryPreview` states
            // this count in the panel showing the rows it counts, and the
            // matched-subject count stays in the live-count panel, which says
            // "subjects match". Raised in review of #177, against a live stack.
            //
            // Held in form state beside `ir` and `resultColumns` so it survives
            // a step change, as they do — the author who goes to step 3 and back
            // should not have to rebuild to see the result again.
            previewRows: rows,
            // Uncapped, unlike `previewRows`. That is the whole reason the far
            // side sends it separately, so the preview says how many rows there
            // are rather than how many fit.
            previewRowCount: Number.isFinite(data?.matchCount) ? data.matchCount : 0,
            // A value from the question the data does not hold, built with a stored one in its
            // place ("I took 'X' to mean 'Y'"). Shown with the preview, never stored.
            substitutions: Array.isArray(data?.substitutions) ? data.substitutions : [],
          },
        };
      });
    } catch (err) {
      if (runId.current !== id) return;
      if (err?.reason) {
        // A classed refusal is not an error banner — it is a message the step
        // renders where the author can act on it. Held in form state for the
        // same reason the query is, so it survives a step change rather than
        // vanishing when the author looks at the recipients and comes back.
        onSetFormData((prev) => ({
          ...prev,
          alert: {
            ...(prev.alert ?? {}),
            sql: null,
            queryDraftId: null,
            window: null,
            resultColumns: [],
            // Cleared with the query they belong to. Rows left behind by a
            // question that has since been refused are a preview of an alert
            // that no longer exists.
            previewRows: [],
            previewRowCount: 0,
            substitutions: [],
            authorRefusal: {
              reason: err.reason,
              construct: err.construct,
              message: err.message,
              values: err.values ?? [],
              confirm: err.confirm ?? [],
              suggest: err.suggest ?? [],
            },
          },
        }));
      } else {
        setError(err?.message || 'Could not build the alert query.');
      }
    } finally {
      clearTimeout(slowTimer);
      if (runId.current === id) {
        setLoading(false);
        setSlow(false);
      }
    }
  }, [onSetFormData]);

  // **The values kept or chosen so far, held in form state beside the refusal they answer.**
  // A second refusal for another value adds to them rather than dropping the first, and a
  // new question starts over. Not a ref: the wizard unmounts this step on Next, and the
  // refusal banner survives that, so a ref here was lost while the banner asking for more
  // was still on screen.
  const choose = useCallback(({ question, datasetIds, values, accepted }) => {
    onSetFormData((prev) => ({
      ...prev,
      alert: { ...(prev.alert ?? {}), authorChoices: { question, values, accepted } },
    }));
    return run({ question, datasetIds, confirmedValues: values, acceptedValues: accepted });
  }, [onSetFormData, run]);

  // Keep `values` as written, together with any already kept or chosen for this question.
  const confirm = useCallback(({ question, datasetIds, values = [] }) => {
    const earlier = choicesFor(choices, question);
    return choose({
      question, datasetIds, values: mergeConfirmed(earlier.values, values), accepted: earlier.accepted,
    });
  }, [choices, choose]);

  // Use the stored values a refusal offered instead, together with any already chosen or
  // kept for this question.
  const accept = useCallback(({ question, datasetIds, values = [] }) => {
    const earlier = choicesFor(choices, question);
    return choose({
      question, datasetIds, values: earlier.values, accepted: mergeAccepted(earlier.accepted, values),
    });
  }, [choices, choose]);

  // Build the question again, sending what the author already kept or chose for it, so a
  // rebuild does not refuse a value they have answered for.
  const build = useCallback(({ question, datasetIds }) => {
    const earlier = choicesFor(choices, question);
    return run({
      question, datasetIds, confirmedValues: earlier.values, acceptedValues: earlier.accepted,
    });
  }, [choices, run]);

  return { run, build, confirm, accept, loading, slow, error };
};

export default useAlertIrAuthor;
